import { useState, useCallback } from 'react';

import useSubmitClaimMutation from './useSubmitClaimMutation';
import { ClaimContext, SubmitClaimEntryPoint } from '../../parcel-finder/claims/types';
import { ClaimableShipment } from '../../../api/types';
import useSaveClaimDraftMutation from './useSaveClaimDraftMutation';
import { createEmptyClaimContext, createClaimContextFromShipment } from './createClaimContext';
import { useFlag } from '@unleash/proxy-client-react';
import { useClaimsShopSettings } from '../../parcel-finder/ClaimsShopSettingsContext';

type UseSubmitClaimOptions = {
  entryPoint: SubmitClaimEntryPoint;
  draftId?: number;
  shipment?: ClaimableShipment;
};

export type Flags = {
  isNewSubmissionFlow: boolean;
  isOrderItemTaxRate: boolean;
};

// DTO is build in submitClaimDTO.ts
function useSubmitClaim(options: UseSubmitClaimOptions) {
  const { shipment, entryPoint, draftId } = options;

  const isClaimsCenterNewSubmissionFlowEnabled = useFlag('claims-center-new-submission-flow');
  const isParcelFinderNewSubmissionFlowEnabled = useFlag('parcel-finder-new-submission-flow');
  const isNewSubmissionFlow =
    entryPoint === SubmitClaimEntryPoint.CLAIMS_CENTER
      ? isClaimsCenterNewSubmissionFlowEnabled
      : isParcelFinderNewSubmissionFlowEnabled;

  const shopSettings = useClaimsShopSettings();
  const flags: Flags = {
    isNewSubmissionFlow,
    isOrderItemTaxRate: shopSettings.isOrderItemLevelTaxEnabled,
  };

  const [claim, setClaim] = useState<ClaimContext>(() => {
    const context = shipment
      ? createClaimContextFromShipment(entryPoint, shipment, flags, shopSettings)
      : createEmptyClaimContext(entryPoint, flags, shopSettings);
    return { ...context, draftId };
  });

  const { mutateAsync: submitClaim, isLoading: isClaimSubmitting } = useSubmitClaimMutation({
    claim,
  });

  const { mutateAsync: saveClaimDraft, isLoading: isClaimDraftSaving } = useSaveClaimDraftMutation(claim);

  const changeClaim = useCallback((value: Partial<ClaimContext>) => {
    setClaim((prevValue) => ({ ...prevValue, ...value }));
  }, []);

  const resetClaim = useCallback(
    () => setClaim((claim) => ({ ...createEmptyClaimContext(entryPoint, flags, shopSettings), type: claim.type })),
    [entryPoint, flags, shopSettings]
  );

  return {
    claim,
    isClaimSubmitting,
    isClaimDraftSaving,
    changeClaim,
    resetClaim,
    submitClaim,
    saveClaimDraft,
  };
}

export default useSubmitClaim;
